import { Preferences } from "@capacitor/preferences";

const COIN_KEY = "monspot_coin_balance";

// 유효한 코인 값인지 검사하는 타입 가드
function isValidCoinValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export class CoinManager {
  private static instance: CoinManager | null = null;

  private balance = 0;
  private initialized = false;

  // 저장 작업이 겹치지 않도록 직렬화
  private writeQueue: Promise<void> = Promise.resolve();

  private constructor() {}

  static getInstance(): CoinManager {
    if (!CoinManager.instance) {
      CoinManager.instance = new CoinManager();
    }
    return CoinManager.instance;
  }

  /**
   * 앱 시작 시 1회 호출 권장
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      const result = await Preferences.get({ key: COIN_KEY });
      const parsed = Number(result.value); // 숫자 변환

      this.balance = isValidCoinValue(parsed) ? Math.floor(parsed) : 0;
    } catch (error) {
      console.error("[CoinManager] init failed:", error);
      this.balance = 0;
    }

    this.initialized = true;
  }

  /**
   * 초기화 여부 확인
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 현재 코인 잔액 반환
   * init 이후 사용 권장
   */
  getBalance(): number {
    return this.balance;
  }

  /**
   * 강제로 코인 값 설정
   */
  async setCoins(amount: number): Promise<number> {
    this.ensureInitialized();

    const next = this.normalize(amount);
    this.balance = next;
    await this.persist();

    return this.balance;
  }

  /**
   * 코인 증가
   */
  async addCoins(amount: number): Promise<number> {
    this.ensureInitialized();

    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("addCoins amount must be a positive number.");
    }

    this.balance = this.normalize(this.balance + amount);
    await this.persist();

    return this.balance;
  }

  /**
   * 코인 사용
   * 성공 시 true, 부족하면 false
   */
  async spendCoins(amount: number): Promise<boolean> {
    this.ensureInitialized();

    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("spendCoins amount must be a positive number.");
    }

    if (this.balance < amount) {
      return false;
    }

    this.balance = this.normalize(this.balance - amount);
    await this.persist();

    return true;
  }

  /**
   * 디버그/테스트용 초기화
   */
  async reset(): Promise<void> {
    this.ensureInitialized();

    this.balance = 0;
    await this.persist();
  }

  // 정수화
  private normalize(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.floor(value));
  }

  // init 전 호출 방어 함수
  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "CoinManager is not initialized. Call await coinManager.init() first.",
      );
    }
  }

  /**
   * 저장 요청 직렬화
   */
  private async persist(): Promise<void> {
    const value = String(this.balance);

    this.writeQueue = this.writeQueue.then(async () => {
      try {
        await Preferences.set({
          key: COIN_KEY,
          value,
        });
      } catch (error) {
        console.error("[CoinManager] persist failed:", error);
        throw error;
      }
    });

    return this.writeQueue;
  }
}

export const coinManager = CoinManager.getInstance();
